// Layer 3 (Transformation): the case page's envelope, stepper, chart and FMEA reads.
// Pure projection only; approval mutations stay in FmeaLearningEngine.

import type { AnalysisRequest, AnalysisResult } from '@/shared/contracts/analytics/v1';
import type { Case, FailureMode } from '@/versions/v1/schema/case';
import type { SeriesTime } from '@/versions/v1/schema/reading-series';
import type { SessionState } from '@/versions/v1/schema/scenario';
import type { ControlChartModel } from '@/versions/v1/modules/_viz';
import type { DriverTreeSeed } from '@/versions/v1/schema/driver-tree';
import type { Diagnosis } from '@/versions/v1/shared/DiagnosisEngine';
import { caseIdForVariable, variableIdForCase } from '@/versions/v1/shared/threads';
import { analysisDiagnosisRows } from '@/versions/v1/modules/advisory/AnalysisRunDiagnosis.transform';
import {
  analysisSignalChart,
  envelopeClock,
  expectationRows,
  firedSignals,
  RUN_ESTIMATE_LABEL,
} from '@/versions/v1/modules/advisory/AnalysisRun.transform';
import { formatPipelineSignalRole } from './FlowLanding.transform';
import {
  failureModeIdForCauseId,
  fmeaActionApproval,
  fmeaLearningRow,
  type FmeaLearningApproval,
  type FmeaLearningSessionState,
} from '@/versions/v1/shared/FmeaLearningEngine';
import { oneDecimalPercent, type AgentRecommendation } from './AgentInvestigation.transform';
import type { PipelineCellRead, PipelineStageId } from './PipelineStrip.transform';
import { PIPELINE_STAGES } from './PipelineStrip.transform';
import { zincMarkerStageAt, type CaseStageMilestones } from '@/versions/v1/shared/caseStage';
import { addMinutes, diffMinutes, formatElapsedMinutes } from '@/versions/v1/shared/clock.derive';
import { breadcrumb, issueExposureView, openCountedAmountPerDay, pricedDriverTree } from './DriverTree/Scene.transform';
import { formatDailyA } from './ValueBand.transform';
import { formatCurrencyExact, assertUsd } from '@/versions/v1/shared/money';
import { FAILURE_MODES, RECOMMENDED_ACTIONS } from '@/versions/v1/shared/bunker-hill-advisory-seed';
import { stopActionRead, stopPairRead } from './StopAction.transform';
import { formatDayMonth } from '@/versions/v1/shared/clock.derive';
import { pluralCount } from '@/versions/v1/shared/text.derive';
import { controlPlanRowName } from '@/versions/v1/shared/bunker-hill-control-plan-seed';
export { pluralCount };

export type CasePageFact = {
  label: string;
  value: string;
  comparison: string;
};

export type CasePageRead = {
  header: {
    title: string;
    code: string;
    severity: string;
    circuit: string;
    value?: string;
    /** The figure's own name: the header prices the run estimate, not the share. */
    valueLabel?: string;
    valueAmountPerDay?: number;
    valueComparison?: string;
    /** A5: the case's driver-tree path from its signal up to EBITDA (`Lime silo LT-500 → … → EBITDA`). */
    valuePath?: readonly string[];
  };
  activeStage: PipelineStageId | null;
  cells: Readonly<Record<PipelineStageId, PipelineCellRead>>;
  facts: readonly CasePageFact[];
  chart: ControlChartModel | null;
  chartYDomain: { min: number; max: number } | null;
  controlPlanLabel?: string;
  recovery: { observed: number; expected: number; shortfall: number } | null;
  diagnosisRows: ReturnType<typeof analysisDiagnosisRows>;
  fmea?: { mode: FailureMode; row: ReturnType<typeof fmeaLearningRow> };
  fmeaMode: FailureMode | null;
  /** Fixed-band cases: the frame's first sample above Band high (the handoff trend tints from here). */
  bandCrossingAt?: SeriesTime;
};

type FiredSignalRead = ReturnType<typeof firedSignals>[number];
type RawSignal = NonNullable<AnalysisResult['spc_signals']>[number];

function percent(value: number): string {
  return `${oneDecimalPercent(value)}%`;
}

function envelopeDailyValue(amount: number, currency: string): string {
  // One-basis demo path (D14 / K7): the analytics envelope prices on the same
  // US$ basis as every other demo-path surface, so the header reads the shared
  // formatter and carries no ISO code and no empty dollar sign. A non-USD
  // envelope throws here rather than printing as US$.
  assertUsd(currency);
  return `${formatCurrencyExact(amount)}/day`;
}

function emptyCells(): Record<PipelineStageId, PipelineCellRead> {
  return Object.fromEntries(
    PIPELINE_STAGES.map(({ id }) => [id, { count: '', comparison: '' }]),
  ) as Record<PipelineStageId, PipelineCellRead>;
}

function caseBranchLabel(caseItem: Case): string {
  const branch: Record<Case['branch'], string> = {
    'zero-touch': 'Zero-touch',
    ask: 'Evidence request',
    escalate: 'Escalate',
  };
  return branch[caseItem.branch];
}

function roleLabel(value: string): string {
  return value.replace(/-/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function actionStatusLabel(value: string): string {
  return value.replace(/-/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function publicCaseText(value: string): string {
  return value.replace(/\bfm-[a-z0-9][a-z0-9-]*\b/gi, (id) =>
    FAILURE_MODES.find((row) => row.id.toLowerCase() === id.toLowerCase())?.failureMode ?? 'linked failure mode',
  ).replace(/\b(?:cp|tarp|rl|fmea)-[a-z0-9][a-z0-9-]*\b/gi, 'linked row');
}

function sentenceCase(value: string): string {
  return value.length === 0 ? value : `${value.charAt(0).toUpperCase()}${value.slice(1)}`;
}

function stageReached(activeStage: PipelineStageId | null, stage: PipelineStageId): boolean {
  if (!activeStage) return false;
  return PIPELINE_STAGES.findIndex((row) => row.id === stage) <=
    PIPELINE_STAGES.findIndex((row) => row.id === activeStage);
}

function engineSignalsForCase(diagnosis: Diagnosis | undefined, caseItem: Case) {
  const variableId = variableIdForCase(caseItem);
  if (!diagnosis || !variableId) return [];
  return diagnosis.signals.filter((signal) =>
    signal.mapping.seriesId && `mv-${signal.mapping.seriesId.replace(/^rs-/, '')}` === variableId,
  );
}

function engineCandidatesForCase(diagnosis: Diagnosis | undefined, caseItem: Case) {
  const variableId = variableIdForCase(caseItem);
  if (!diagnosis || !variableId) return [];
  return diagnosis.candidates.filter((candidate) => candidate.excursion.variableId === variableId);
}

function engineCaseForCase(diagnosis: Diagnosis | undefined, caseItem: Case) {
  const variableId = variableIdForCase(caseItem);
  if (!diagnosis || !variableId) return undefined;
  return diagnosis.cases.find((row) => row.id === caseIdForVariable(variableId));
}

function fmeaModeForResult(result: AnalysisResult | undefined): FailureMode | null {
  const causeId = result?.diagnosis?.candidates[0]?.cause_id;
  const failureModeId = causeId ? failureModeIdForCauseId(causeId) : undefined;
  return FAILURE_MODES.find((row) => row.id === failureModeId) ?? null;
}

function longestFiredSignal(rows: readonly FiredSignalRead[]): FiredSignalRead | undefined {
  return [...rows].sort((left, right) =>
    right.heldMinutes - left.heldMinutes || left.segmentStart.localeCompare(right.segmentStart),
  )[0];
}

function rawSignalForRead(result: AnalysisResult | undefined, read: FiredSignalRead | undefined): RawSignal | undefined {
  if (!result || !read) return undefined;
  return (result.spc_signals ?? []).find((row) =>
    row.fired && row.rule_id === read.ruleId && envelopeClock(row.segment_start) === read.segmentStart,
  );
}

function eventClock(signal: RawSignal | undefined, edge: 'first' | 'last', fallback: SeriesTime): SeriesTime {
  if (edge === 'first' && signal?.limits?.band_high != null && signal.evidence_points[0]) {
    return envelopeClock(signal.evidence_points[0].event_time) as SeriesTime;
  }
  const stamp = edge === 'first' ? signal?.first_event_time : signal?.last_event_time;
  const pointStamp = edge === 'first'
    ? signal?.evidence_points[0]?.event_time
    : signal?.evidence_points.at(-1)?.event_time;
  return envelopeClock(stamp ?? pointStamp ?? fallback) as SeriesTime;
}

function eventSpan(signal: RawSignal | undefined, start: SeriesTime, end: SeriesTime): string {
  return signal?.limits?.band_high != null
    ? `${formatDayMonth(start)} ${start.slice(11, 16)}–${formatDayMonth(end)} ${end.slice(11, 16)}`
    : `${start.slice(11, 16)}–${end.slice(11, 16)}`;
}

function chartForPrimaryExcursion(
  result: AnalysisResult | undefined,
  slices: AnalysisRequest['slices'] | undefined,
  primary: FiredSignalRead | undefined,
  cursor: SeriesTime,
  spec?: { lo?: number; hi: number },
): { chart: ControlChartModel; yDomain: { min: number; max: number } } | null {
  if (!result || !slices || !primary) return null;
  const signal = rawSignalForRead(result, primary);
  if (!signal) return null;
  const source = analysisSignalChart(signal, slices);
  if (!source) return null;

  const bandHigh = signal.limits?.band_high ?? undefined;
  const firstEvent = bandHigh !== undefined && signal.evidence_points[0]
    ? envelopeClock(signal.evidence_points[0].event_time) as SeriesTime : eventClock(signal, 'first', cursor);
  const lastEvent = eventClock(signal, 'last', firstEvent);
  const visibleLastEvent = lastEvent > cursor ? cursor : lastEvent;
  const contextPoints = bandHigh === undefined ? source.points : [...new Map(
    (result.spc_signals ?? []).filter((row) => row.signal_role === signal.signal_role)
      .flatMap((row) => row.evidence_points).map((point) => [point.event_time, {
        at: envelopeClock(point.event_time), value: point.value, outside: point.value > bandHigh,
      }]),
  ).values()].sort((a, b) => a.at.localeCompare(b.at));
  const windowStart = addMinutes(bandHigh === undefined ? firstEvent : (contextPoints[0]?.at ?? firstEvent) as SeriesTime, -15);
  const windowEnd = addMinutes(visibleLastEvent, 15);
  const points = contextPoints
    .filter((point) => point.at >= windowStart && point.at <= cursor && point.at <= windowEnd)
    .map((point) => ({ ...point, highlighted: point.at === firstEvent }));
  if (points.length === 0) return null;

  const excursionRange = visibleLastEvent >= firstEvent
    ? { startAt: firstEvent, endAt: visibleLastEvent }
    : undefined;
  const chart: ControlChartModel = {
    readingLabel: `${primary.ruleLabel} · ${primary.modeLabel}`,
    points,
    limits: bandHigh === undefined ? { mean: source.centre, ucl: source.ucl, lcl: source.lcl } : undefined,
    bandHigh,
    spec,
    excursionRange,
    timeWindow: { startAt: windowStart, endAt: windowEnd },
    unit: source.unit ?? undefined,
    precision: 2,
  };
  const refs = [
    ...points.map((point) => point.value),
    source.centre,
    source.ucl,
    source.lcl,
    ...(spec ? (spec.lo === undefined ? [spec.hi] : [spec.lo, spec.hi]) : []),
  ];
  return {
    chart,
    yDomain: (() => {
      const min = Math.min(...refs);
      const max = Math.max(...refs);
      const span = max - min || 1;
      return { min: min - span * 0.12, max: max + span * 0.12 };
    })(),
  };
}

function sessionTimeline(
  timeline: CaseStageMilestones,
  approval: FmeaLearningApproval | undefined,
): CaseStageMilestones {
  if (!approval) return timeline;
  const approvedAt = approval.updatedAt.slice(0, 16) as SeriesTime;
  return {
    ...timeline,
    learnAt: timeline.learnAt && timeline.learnAt < approvedAt ? timeline.learnAt : approvedAt,
  };
}

export function casePageRead(input: {
  caseItem: Case;
  result?: AnalysisResult;
  signalResult?: AnalysisResult;
  slices?: AnalysisRequest['slices'];
  diagnosis?: Diagnosis;
  state: SessionState;
  fmeaLearning: FmeaLearningSessionState;
  stageTimeline: CaseStageMilestones;
  driverTreeSeed: DriverTreeSeed;
  recommendation?: AgentRecommendation;
  /**
   * #731: false for the floor seats (Operator, Control room), whose header
   * prices no US$; the header reads the rougher recovery gap instead and the
   * stepper's Value cell drops its counted-share money. Defaults to true.
   */
  showMoney?: boolean;
}): CasePageRead {
  const { caseItem, result, signalResult = result, slices, diagnosis, state, fmeaLearning, stageTimeline, driverTreeSeed, recommendation, showMoney = true } = input;
  const { cursor } = state;
  const pricedTree = pricedDriverTree(driverTreeSeed, state);
  const cells = emptyCells();
  const diagnosisRows = result ? analysisDiagnosisRows(result) : [];
  const engineCandidates = engineCandidatesForCase(diagnosis, caseItem);
  const engineCase = engineCaseForCase(diagnosis, caseItem);
  const engineSignals = engineSignalsForCase(diagnosis, caseItem);
  const stop = stopPairRead(result);
  const stopAction = stopActionRead(caseItem, result);
  const expectations = !caseItem.stopPlan && result && slices ? expectationRows(result, slices) : [];
  const recoveryExpectation = expectations.find((row) => row.signalRole === caseItem.analysisRun?.expectationRole);
  const recovery = recoveryExpectation
    ? {
        observed: recoveryExpectation.observed,
        expected: recoveryExpectation.expected,
        shortfall: recoveryExpectation.shortfall,
      }
    : null;
  const fired = result && slices ? firedSignals(result, slices) : [];
  const primary = longestFiredSignal(fired);
  const primarySignal = rawSignalForRead(result, primary);
  // Signals follows the landing's segment-start convention on the full run.
  const signalFiresAtCursor = caseItem.analysisRun?.milestonesFromFrames ? fired : signalResult && slices
    ? firedSignals(signalResult, slices).filter((row) => row.segmentStart <= cursor)
    : [];
  // Excursions follows Evidence: held time and event stamps come from this cursor frame.
  const excursionFiresAtCursor = result && slices
    ? firedSignals(result, slices).filter((row) => {
        const signal = rawSignalForRead(result, row);
        if (row.segmentStart > cursor) return false;
        return signal?.first_event_time ? envelopeClock(signal.first_event_time) <= cursor : false;
      })
    : [];
  const stepperPrimary = longestFiredSignal(excursionFiresAtCursor);
  const stepperPrimarySignal = rawSignalForRead(result, stepperPrimary);
  const fullRunFallback = signalResult && slices && !stepperPrimary
    ? firedSignals(signalResult, slices)
        .map((row) => ({ row, signal: rawSignalForRead(signalResult, row) }))
        .flatMap(({ row, signal }) => {
          const firstEvent = signal?.first_event_time
            ? eventClock(signal, 'first', cursor)
            : undefined;
          return firstEvent && firstEvent <= cursor ? [{ row, signal, firstEvent }] : [];
        })
        .sort((left, right) => left.firstEvent.localeCompare(right.firstEvent))[0]
    : undefined;
  const topPlan = result?.diagnosis?.candidates[0]?.control_plan;
  const controlPlanLabel = topPlan
    ? controlPlanRowName(topPlan.row_id) ?? formatPipelineSignalRole(topPlan.signal_role)
    : undefined;
  // A Control Plan row may define no low spec (the bearing's `cp-bm-brg-temp`
  // is high-only); the chart then draws the spec high alone.
  const spec = topPlan ? { lo: topPlan.spec_low ?? undefined, hi: topPlan.spec_high } : undefined;
  const chartRead = chartForPrimaryExcursion(result, slices, primary, cursor, spec);
  const chart = chartRead?.chart ?? null;
  const fmeaMode = fmeaModeForResult(result);
  const approval = fmeaLearning.approvals.get(caseItem.id);
  // D19: the action is approved at the first gate; its FMEA change goes live at the second.
  const actionApproved = fmeaActionApproval(fmeaLearning, caseItem.id) !== undefined;
  const timeline = sessionTimeline(stageTimeline, approval);
  const activeStage = zincMarkerStageAt(cursor, timeline);
  const quality = result?.data_quality;
  const qualityConfidence = quality?.confidence != null ? percent(quality.confidence) : undefined;
  const qualityComparison = quality
    ? `vs 100% clean input · ${pluralCount(quality.bad_quality_points, 'bad point')} · ${pluralCount(quality.missing_points, 'missing point')}`
    : undefined;
  const recoveryValue = recovery ? percent(recovery.observed) : undefined;
  const emptyValueCount = `${pluralCount(0, 'value')} captured`;
  // D13: `rd-kpi-zn-rec` is the plant's one current recovery. This fixture
  // expectation is the rougher proxy, so every surface that shows it names the
  // stage rather than reading as a second plant recovery.
  const recoveryComparison = recovery && recoveryExpectation
    ? `rougher recovery ${percent(recovery.observed)} vs ${percent(recovery.expected)} expected · ${oneDecimalPercent(recovery.shortfall)} pt short`
    : undefined;
  const actionSeed = RECOMMENDED_ACTIONS.find((row) => row.caseId === caseItem.id);
  const actionSummary = stopAction?.proposition ?? recommendation?.control_plan_action ?? actionSeed?.text ?? caseItem.ifThen;
  const compactAction = actionSummary.split(/[.;]/)[0]?.trim() ?? actionSummary;
  const fmeaRow = fmeaMode ? fmeaLearningRow(fmeaLearning, fmeaMode) : undefined;
  const caseIssue = pricedTree.issues.find((row) => row.caseId === caseItem.id);
  const caseIssueExposure = caseIssue ? issueExposureView(caseIssue, pricedTree, cursor) : undefined;
  const ownerIssue = caseIssue?.sharedPathContribution
    ? pricedTree.issues.find((row) => row.id === caseIssue.sharedPathContribution?.ownerIssueId)
    : undefined;
  const ownerAmount = ownerIssue && caseIssueExposure?.contributor
    ? openCountedAmountPerDay(ownerIssue, pricedTree, cursor)
    : undefined;
  const valueLabel = !caseItem.stopPlan && caseIssueExposure ? formatDailyA(caseIssueExposure.amountPerDay) : undefined;
  // #731: a floor seat's header prices nothing. The figure slot reads the
  // measured rougher recovery gap in points — the same non-money basis the
  // driver tree's floor projection states as `<n> pt below design` — with the
  // observed and expected recovery under it. Never the envelope's US$.
  const floorHeader = !showMoney && !caseItem.stopPlan && recovery
    ? {
        valueLabel: 'Rougher recovery',
        value: `${oneDecimalPercent(recovery.shortfall)} pt short`,
        valueComparison: `${oneDecimalPercent(recovery.observed)}% vs ${oneDecimalPercent(recovery.expected)}% expected`,
      }
    : undefined;
  // A5: the header's US$ reads up its driver-tree path, leaf (signal) first, EBITDA last.
  const valuePath = caseIssue
    ? breadcrumb(caseIssue, driverTreeSeed.nodes).map((node) => node.label).reverse()
    : undefined;
  const ownerComparison = ownerIssue && ownerAmount !== undefined
    ? `within ${formatDailyA(ownerAmount)} ${ownerIssue.shortLabel} owner`
    : caseIssueExposure?.contributor ? 'contributor share in counted owner exposure' : undefined;
  const valueComparison = recoveryComparison;
  const envelopeValue = !caseItem.stopPlan && result?.financial_impact
    ? envelopeDailyValue(result.financial_impact.impact_per_day, result.financial_impact.currency)
    : undefined;
  const engineEvents = engineSignals.flatMap((signal) => signal.spc?.events ?? [])
    .filter((event) => event.detectedAt <= cursor);
  const engineEventIds = new Set(engineEvents.map((event) => event.id));
  const engineRuleLabels = [...new Set(engineEvents.map((event) => event.rule.replace(/-/g, ' ')))];
  const stageCode = caseItem.code ?? caseItem.id;

  if (stageReached(activeStage, 'plant-data')) {
    if (quality) {
      const signalRoleCount = quality.role_counts?.length ?? 0;
      const tags = slices?.signal_map ?? [];
      cells['plant-data'] = {
        count: `${pluralCount(tags.length || signalRoleCount, 'tag')}${qualityConfidence ? ` · ${qualityConfidence} confidence` : ''}`,
        comparison: qualityComparison ?? 'mapped against plant I/O list',
      };
    } else {
      cells['plant-data'] = {
        count: pluralCount(engineSignals.length, 'signal tag'),
        comparison: `against ${pluralCount(1, `${stageCode} case observation`)}`,
      };
    }
  }
  if (stageReached(activeStage, 'signals')) {
    if (signalResult && slices) {
      const firedRuleIds = new Set(signalFiresAtCursor.map((row) => row.ruleId));
      const firedRuleLabels = [...new Set(signalFiresAtCursor.map((row) => row.ruleLabel))];
      cells.signals = {
        count: `${pluralCount(firedRuleIds.size, 'rule')} fired`,
        comparison: firedRuleLabels.join(' · ') || `vs ${pluralCount(1, `${stageCode} case observation`)}`,
      };
    } else {
      const firedRuleLabels = engineRuleLabels;
      cells.signals = {
        count: `${pluralCount(firedRuleLabels.length, 'rule')} fired`,
        comparison: firedRuleLabels.join(' · ') || `vs ${pluralCount(1, `${stageCode} case observation`)}`,
      };
    }
  }
  if (stageReached(activeStage, 'excursions')) {
    if (stepperPrimary && stepperPrimarySignal) {
      const tierOneMinutes = result?.diagnosis?.candidates[0]?.tarp?.escalate_after_minutes;
      const eventStart = eventClock(stepperPrimarySignal, 'first', cursor);
      const eventEnd = eventClock(stepperPrimarySignal, 'last', eventStart);
      const visibleEnd = eventEnd > cursor ? cursor : eventEnd;
      cells.excursions = {
        count: `${stepperPrimary.heldLabel} held`,
        comparison: tierOneMinutes === undefined
          ? eventSpan(stepperPrimarySignal, eventStart, visibleEnd)
          : `${eventSpan(stepperPrimarySignal, eventStart, visibleEnd)} · vs ${formatElapsedMinutes(tierOneMinutes)} response`,
      };
    } else if (fullRunFallback?.signal) {
      const { firstEvent, signal } = fullRunFallback;
      const eventEnd = eventClock(signal, 'last', firstEvent);
      const visibleEnd = eventEnd > cursor ? cursor : eventEnd;
      const elapsedMinutes = diffMinutes(cursor, firstEvent);
      const heldMinutes = Math.min(signal.held_minutes ?? 0, elapsedMinutes);
      const tierOneMinutes = result?.diagnosis?.candidates[0]?.tarp?.escalate_after_minutes;
      const span = `${firstEvent.slice(11, 16)}–${visibleEnd.slice(11, 16)}`;
      cells.excursions = {
        count: `${formatElapsedMinutes(heldMinutes)} held`,
        comparison: tierOneMinutes === undefined
          ? span
          : `${span} · vs ${formatElapsedMinutes(tierOneMinutes)} response`,
      };
    } else {
      cells.excursions = {
        count: pluralCount(engineEventIds.size, 'excursion'),
        comparison: engineEventIds.size > 0
          ? engineRuleLabels.join(' · ')
          : `vs ${pluralCount(Math.max(1, engineSignals.length), 'case-linked signal')}`,
      };
    }
  }
  if (stageReached(activeStage, 'cases')) {
    cells.cases = {
      count: caseBranchLabel(caseItem),
      comparison: `to ${roleLabel(caseItem.assignedTo)} · ${roleLabel(caseItem.severity)} severity`,
    };
  }
  if (stageReached(activeStage, 'diagnosis')) {
    if (diagnosisRows[0]) {
      cells.diagnosis = {
        count: diagnosisRows[0].causeLabel,
        comparison: `${diagnosisRows[0].probabilityLabel} · ${pluralCount(diagnosisRows.length, 'ranked candidate')}`,
      };
    } else if (engineCandidates[0]) {
      const first = engineCandidates[0];
      const next = engineCandidates[1];
      const probability = `${oneDecimalPercent(first.confidencePct / 100)}%`;
      cells.diagnosis = {
        count: first.rule.diagnosis,
        comparison: next
          ? `${probability} vs next ${oneDecimalPercent(next.confidencePct / 100)}% · ${pluralCount(engineCandidates.length, 'ranked candidate')}`
          : `${probability} · ${pluralCount(engineCandidates.length, 'ranked candidate')}`,
      };
    } else {
      cells.diagnosis = {
        count: engineCase
          ? sentenceCase(publicCaseText(engineCase.causeCandidate.replace(/^No structured rule matched — /, '')))
          : pluralCount(0, 'ranked candidate'),
        comparison: engineCandidates.length === 0
          ? `${pluralCount(0, 'ranked cause')} · ${pluralCount(engineEventIds.size, 'case-linked excursion')}`
          : `${pluralCount(engineCandidates.length, 'ranked candidate')} · vs ${pluralCount(1, `${stageCode} case observation`)}`,
      };
    }
  }
  if (stageReached(activeStage, 'action')) {
    cells.action = actionSeed || recommendation || stopAction
      ? {
          count: actionApproved ? stopAction ? 'Stop planned' : 'Approved' : actionSeed ? actionStatusLabel(actionSeed.status) : 'Proposed',
          comparison: publicCaseText(compactAction),
        }
      : { count: pluralCount(0, 'action'), comparison: `vs ${pluralCount(1, `${stageCode} case recommendation`)}` };
  }
  if (stageReached(activeStage, 'value')) {
    const countedShare = showMoney && caseIssueExposure?.contributor
      ? {
          money: `${valueLabel} counted share`,
          moneyComparison: ownerComparison,
        }
      : {};
    cells.value = !caseItem.stopPlan && (recovery || caseIssueExposure)
      ? {
          count: recovery ? `${percent(recovery.observed)} rougher recovery` : valueLabel ?? emptyValueCount,
          comparison: recoveryComparison ?? (caseIssueExposure ? 'case exposure feeds recovery value' : `vs ${stageCode} case outcome`),
          ...countedShare,
        }
      : { count: emptyValueCount, comparison: `vs ${stageCode} case outcome` };
  }
  if (stageReached(activeStage, 'learn')) {
    cells.learn = fmeaMode && fmeaRow
      ? {
          count: fmeaMode.failureMode,
          comparison: `RPN ${fmeaRow.rpn} = ${fmeaRow.s} × ${fmeaRow.o} × ${fmeaRow.d} · ${pluralCount(fmeaRow.evidenceCount, 'evidence case')}`,
        }
      : { count: pluralCount(0, 'FMEA update'), comparison: `vs ${pluralCount(1, `${stageCode} case learning record`)}` };
  }

  // W3d: the one-off pair is stated once, in the handoff region. The stepper
  // never shows the future, so Value stays blank at Action and, once reached,
  // reads the generic zero: planning a stop captures nothing (D16).

  const facts: CasePageFact[] = [];
  if (topPlan) {
    const limit = topPlan.spec_low == null
      ? `${topPlan.spec_high.toFixed(2)} ${topPlan.unit} high`
      : `${topPlan.spec_low.toFixed(2)}–${topPlan.spec_high.toFixed(2)} ${topPlan.unit}`;
    const lcl = primarySignal?.limits?.lcl;
    facts.push({
      label: 'Control Plan',
      value: limit,
      comparison: topPlan.band_high != null ? `Band high ${topPlan.band_high} ${topPlan.unit}` : lcl == null ? `${controlPlanLabel} operating band` : `vs statistical LCL ${lcl.toFixed(2)} ${topPlan.unit}`,
    });
  }
  if (primary) {
    const eventStart = primarySignal ? eventClock(primarySignal, 'first', cursor) : cursor;
    const eventEnd = primarySignal ? eventClock(primarySignal, 'last', eventStart) : eventStart;
    const visibleEnd = eventEnd > cursor ? cursor : eventEnd;
    const tierOneMinutes = result?.diagnosis?.candidates[0]?.tarp?.escalate_after_minutes;
    facts.push({
      label: 'Excursion',
      value: `${primary.heldLabel} held`,
      comparison: tierOneMinutes === undefined
        ? eventSpan(primarySignal, eventStart, visibleEnd)
        : `vs ${formatElapsedMinutes(tierOneMinutes)} first-tier response · ${eventSpan(primarySignal, eventStart, visibleEnd)}`,
    });
  }
  if (recovery && recoveryValue && recoveryComparison) {
    facts.push({ label: 'Rougher recovery', value: recoveryValue, comparison: recoveryComparison });
  }
  const lowestPoint = chart?.points.reduce<(typeof chart.points)[number] | undefined>((lowest, point) =>
    !lowest || point.value < lowest.value ? point : lowest, undefined);
  if (lowestPoint) {
    facts.push({ label: 'Lowest reading', value: `${lowestPoint.value.toFixed(2)} ${chart?.unit ?? ''}`,
      comparison: `${formatDayMonth(lowestPoint.at as SeriesTime)} ${lowestPoint.at.slice(11, 16)} · ${topPlan?.band_high != null ? `Band high ${topPlan.band_high} ${topPlan.unit}` : `vs Control Plan low ${topPlan?.spec_low?.toFixed(2)} ${topPlan?.unit}`}` });
  }
  // W3d: the crossing is the fixed-band rule's own first evidence point. The
  // frames carry that evidence only once the rule fires (08 Jun 03:00, the 24 h
  // hold), so before then there is no crossing fact. The longest fired signal
  // can be the trend rule, whose evidence starts at the settle (05 Jun), not at
  // the crossing, so it is never read for this fact.
  const bandSignal = topPlan?.band_high != null
    ? (result?.spc_signals ?? []).find((row) => row.limits?.band_high != null && row.evidence_points.length > 0)
    : undefined;
  let bandCrossingAt: SeriesTime | undefined;
  if (topPlan?.band_high != null && bandSignal?.evidence_points[0]) {
    const crossing = envelopeClock(bandSignal.evidence_points[0].event_time) as SeriesTime;
    bandCrossingAt = crossing;
    const holdAt = bandSignal.fired && bandSignal.first_event_time ? envelopeClock(bandSignal.first_event_time) as SeriesTime : undefined;
    facts.push({ label: 'Band crossing', value: `${formatDayMonth(crossing)} ${crossing.slice(11, 16)}`, comparison: holdAt ? `Band high ${topPlan.band_high} ${topPlan.unit} · hold met ${formatDayMonth(holdAt)} ${holdAt.slice(11, 16)}` : `Band high ${topPlan.band_high} ${topPlan.unit}` });
  }
  const latestPoint = chart?.points.at(-1);
  if (stop && latestPoint && topPlan) {
    facts.push({ label: 'Latest reading', value: `${latestPoint.value.toFixed(2)} ${topPlan.unit}`, comparison: `vs Band high ${topPlan.band_high} ${topPlan.unit} · spec high ${topPlan.spec_high} ${topPlan.unit}` });
  } else if (quality && qualityConfidence && qualityComparison) {
    facts.push({ label: 'Input quality', value: `${qualityConfidence} confidence`, comparison: qualityComparison });
  }
  if (!result) {
    facts.push({
      label: 'Case evidence',
      value: publicCaseText(caseItem.observation),
      comparison: publicCaseText(caseItem.causeCandidate),
    });
  }

  // W3d: a stop case's header figure is its reading against Band high (comp
  // Handoff A); the one-off pair lives once, in the handoff region.
  const bandReading = caseItem.stopPlan && latestPoint && topPlan?.band_high != null
    ? {
        value: `${latestPoint.value.toFixed(1)} ${topPlan.unit}`,
        comparison: `vs Band high ${topPlan.band_high} ${topPlan.unit} · ${latestPoint.value >= topPlan.band_high ? '+' : '−'}${Math.abs(latestPoint.value - topPlan.band_high).toFixed(1)} ${topPlan.unit}`,
      }
    : undefined;

  return {
    header: {
      title: caseItem.title,
      code: caseItem.code ?? caseItem.id,
      severity: roleLabel(caseItem.severity),
      circuit: caseItem.circuit,
      value: caseItem.stopPlan ? bandReading?.value : floorHeader ? floorHeader.value : envelopeValue,
      valueLabel: caseItem.stopPlan ? undefined : floorHeader ? floorHeader.valueLabel : envelopeValue ? RUN_ESTIMATE_LABEL : undefined,
      valueAmountPerDay: caseItem.stopPlan || floorHeader ? undefined : result?.financial_impact?.impact_per_day,
      valueComparison: caseItem.stopPlan ? bandReading?.comparison : floorHeader ? floorHeader.valueComparison : valueComparison,
      valuePath: envelopeValue && valuePath && valuePath.length > 1 ? valuePath : undefined,
    },
    activeStage,
    cells,
    facts,
    chart,
    chartYDomain: chartRead?.yDomain ?? null,
    controlPlanLabel,
    recovery,
    diagnosisRows,
    fmea: fmeaMode && fmeaRow ? { mode: fmeaMode, row: fmeaRow } : undefined,
    fmeaMode,
    bandCrossingAt,
  };
}
